import type { IconName } from '../shared/components/Icon';

export interface NavigationItem {
  readonly to: string;
  readonly labelKey: string;
  readonly icon: IconName;
}

/**
 * A row that carries no address of its own: it names one part of the console and folds the
 * destinations that belong to it. The `id` is what the rail remembers between visits, so a
 * section stays in the state the reader left it in.
 */
export interface NavigationSection {
  readonly id: string;
  readonly labelKey: string;
  readonly icon: IconName;
  readonly items: readonly NavigationItem[];
}

export type NavigationEntry = NavigationItem | NavigationSection;

export function isNavigationSection(entry: NavigationEntry): entry is NavigationSection {
  return 'items' in entry;
}

/** Every address behind a list of entries, with the sections flattened away. */
export function navigationLeaves(entries: readonly NavigationEntry[]): readonly NavigationItem[] {
  return entries.flatMap((entry) => (isNavigationSection(entry) ? [...entry.items] : [entry]));
}

/**
 * The console's own map, kept apart from the shell that draws it because three things
 * read it: the navigation rail, the command palette, and the start page a workspace
 * shows when every document has been closed. Only the rail draws the sections; the other
 * two read the addresses underneath them through `navigationLeaves`.
 */
export const primaryNavigation: readonly NavigationEntry[] = [
  { to: '/console', labelKey: 'nav.dashboard', icon: 'dashboard' },
  { to: '/console/experiences', labelKey: 'app.workspace', icon: 'sparkle' },
  {
    id: 'accounting',
    labelKey: 'nav.accounting',
    icon: 'journal',
    items: [
      { to: '/console/journals', labelKey: 'nav.journals', icon: 'journal' },
      { to: '/console/journals/new', labelKey: 'journals.newJournal', icon: 'plus' },
      { to: '/console/ledger', labelKey: 'nav.ledger', icon: 'reports' },
      { to: '/console/accounts', labelKey: 'nav.accounts', icon: 'accounts' },
    ],
  },
  {
    id: 'baseData',
    labelKey: 'nav.baseData',
    icon: 'user',
    items: [
      { to: '/console/persons', labelKey: 'nav.persons', icon: 'user' },
      { to: '/console/identity', labelKey: 'nav.interOrganizationIdentity', icon: 'sparkle' },
      { to: '/console/currency/rates', labelKey: 'nav.exchangeRates', icon: 'exchange' },
      { to: '/console/currency/administration', labelKey: 'nav.currencyAdministration', icon: 'exchange' },
    ],
  },
  { to: '/console/inventory', labelKey: 'nav.inventory', icon: 'inventory' },
  { to: '/console/reports', labelKey: 'nav.reports', icon: 'reports' },
];

/** Operational documents and the master data used to create them. */
export const operationsNavigation: readonly NavigationEntry[] = [
  { to: '/console/automation', labelKey: 'nav.automation', icon: 'exchange' },
  {
    id: 'sales',
    labelKey: 'nav.sales',
    icon: 'sales',
    items: [
      { to: '/console/sales', labelKey: 'nav.salesOrders', icon: 'sales' },
      { to: '/console/customers', labelKey: 'nav.customers', icon: 'user' },
    ],
  },
  { to: '/console/receivables', labelKey: 'nav.receivables', icon: 'receivables' },
  {
    id: 'purchasing',
    labelKey: 'nav.purchasing',
    icon: 'purchase',
    items: [
      { to: '/console/purchasing', labelKey: 'nav.purchaseOrders', icon: 'purchase' },
      { to: '/console/suppliers', labelKey: 'nav.suppliers', icon: 'user' },
    ],
  },
  { to: '/console/payables', labelKey: 'nav.payables', icon: 'payables' },
  { to: '/console/treasury', labelKey: 'nav.treasury', icon: 'cash' },
  { to: '/console/personal-finance', labelKey: 'nav.personalFinance', icon: 'cash' },
  { to: '/console/tax', labelKey: 'nav.tax', icon: 'reports' },
  { to: '/console/fixed-assets', labelKey: 'nav.fixedAssets', icon: 'inventory' },
  { to: '/console/budgeting', labelKey: 'nav.budgeting', icon: 'trend' },
  { to: '/console/costing', labelKey: 'nav.costing', icon: 'trend' },
  { to: '/console/construction', labelKey: 'constructionWorkspace.nav', icon: 'trend' },
  { to: '/console/workforce', labelKey: 'nav.workforce', icon: 'user' },
  { to: '/console/payroll', labelKey: 'nav.payroll', icon: 'payroll' },
  { to: '/console/projects', labelKey: 'nav.projects', icon: 'trend' },
];

export const administrationNavigation: readonly NavigationItem[] = [
  { to: '/console/settings/access', labelKey: 'nav.access', icon: 'user' },
  { to: '/console/settings/audit', labelKey: 'auditLog.title', icon: 'reports' },
  { to: '/console/settings', labelKey: 'nav.settings', icon: 'settings' },
];

/**
 * The four destinations a phone gets in one thumb reach. The rest of the workspace stays
 * one tap away behind "More", which opens the same drawer the menu button does.
 */
export const mobileTabs: readonly NavigationItem[] = [
  { to: '/console', labelKey: 'nav.dashboard', icon: 'dashboard' },
  { to: '/console/journals', labelKey: 'nav.journals', icon: 'journal' },
  { to: '/console/ledger', labelKey: 'nav.shortLedger', icon: 'reports' },
  { to: '/console/treasury', labelKey: 'nav.treasury', icon: 'cash' },
];

export const allNavigation: readonly NavigationItem[] = [
  ...navigationLeaves(primaryNavigation),
  ...navigationLeaves(operationsNavigation),
  ...administrationNavigation,
];
